import type { Assignment } from '../Assignment';
import { vehicleParcelIcon } from '../../../shared/Icons/VehicleIcons';

export type VehicleLimitExceededType = {
    exceeded: boolean | null;
    maxExceeded: boolean | null;
    warningThresholdExceeded: boolean | null;
    limit: number;
    value: number;
    maxValue: number | null;
    type: string;
    src: string;
    alt: string;
    color: string;
    doubtReason: string | null;
    title: string;
    count: number | null;
};

export class VehicleLimitItem {

    public static _VEHICLE_LIMIT_COLOR_UNSET: string = '#1c2f1033';
    public static _VEHICLE_LIMIT_COLOR_SUCCESS: string = '#4caf50';
    public static _VEHICLE_LIMIT_COLOR_WARNING: string = '#cb7409';
    public static _VEHICLE_LIMIT_COLOR_ERROR: string = '#cb0936';

    public static _ALERT_ICON_TIME_WINDOW: string = '<svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 -0.5 11.51 16.7644"><g transform="translate(-635.788 -199.182)"><path d="M16.424,7.818a5.2,5.2,0,0,0-1.981-4.085L13.818,0H8.606L7.987,3.733a5.193,5.193,0,0,0,0,8.17l.619,3.733h5.212l.625-3.733A5.2,5.2,0,0,0,16.424,7.818Zm-9.121,0a3.909,3.909,0,1,1,3.909,3.909A3.912,3.912,0,0,1,7.3,7.818Z" transform="translate(629.788 199.182)" fill="{color}"/><rect width="2" height="4" rx="1" transform="translate(640 204)" fill="{color}"/><rect width="2" height="1.5" rx="0.75" transform="translate(640 208.5)" fill="{color}"/></g></svg>'
    public static _ALERT_ICON_WORK_TIME: string = '<svg xmlns="http://www.w3.org/2000/svg" height="24px" viewBox="0 -960 960 960" width="24px" fill="{color}"><path d="M360-840v-80h240v80H360Zm80 440h80v-240h-80v240Zm40 320q-74 0-139.5-28.5T226-186q-49-49-77.5-114.5T120-440q0-74 28.5-139.5T226-694q49-49 114.5-77.5T480-800q62 0 119 20t107 58l56-56 56 56-56 56q38 50 58 107t20 119q0 74-28.5 139.5T734-186q-49 49-114.5 77.5T480-80Zm0-80q116 0 198-82t82-198q0-116-82-198t-198-82q-116 0-198 82t-82 198q0 116 82 198t198 82Zm0-280Z"/></svg>'
    public static _ALERT_ICON_TOTAL_WEIGHT: string = '<svg xmlns="http://www.w3.org/2000/svg" height="32" viewBox="0 -960 960 960" width="32"><path d="M240-200h480l-57-400H297l-57 400Zm240-480q17 0 28.5-11.5T520-720q0-17-11.5-28.5T480-760q-17 0-28.5 11.5T440-720q0 17 11.5 28.5T480-680Zm113 0h70q30 0 52 20t27 49l57 400q5 36-18.5 63.5T720-120H240q-37 0-60.5-27.5T161-211l57-400q5-29 27-49t52-20h70q-3-10-5-19.5t-2-20.5q0-50 35-85t85-35q50 0 85 35t35 85q0 11-2 20.5t-5 19.5ZM240-200h480-480Z" fill="{color}"/></svg>';
    public static _ALERT_ICON_PARCELS: string = vehicleParcelIcon

    public static getCountTimeWindowExceeded(assignment: Assignment, source: string = 'base_route'): number {
        let count = 0;
        let alerts = this.getAlerts(assignment, source)?.time_window;
        if (alerts && Object.keys(alerts).length) {
            for (let jobId in alerts) {
                if (!alerts[jobId].fulfilled) {
                    count++;
                }
            }
        }
        return count;
    }

    public static hasTimeWindows(assignment: Assignment, source: string = 'base_route'): boolean {
        let alerts = this.getAlerts(assignment, source)?.time_window;
        return !!alerts && Object.keys(alerts).length > 0;
    }

    public static getRouteDurationSeconds(assignment: Assignment, source: string = 'base_route'): number {
        let routeInfo = assignment.getRouteInfo();
        if (source === 'base_route') {
            return (routeInfo?.route?.eta_with_stops_m || routeInfo?.estimate?.eta_with_stops_m || 0) * 60;
        } else if (source === 'current_in_realisation') {
            return (routeInfo?.current?.eta_with_stops_m || 0) * 60;
        } else if (source === 'modified_by_operator') {
            return (assignment.getRouteModifiedByOperator()?.eta_with_stops_m || 0) * 60;
        } else {
            return 0;
        }
    }

    public static getRouteStartSeconds(assignment: Assignment): number {
        return assignment?.getStartHourRouteInSeconds();
    }

    public static getRouteEndSeconds(assignment: Assignment, source: string = 'base_route'): number {
        return this.getRouteStartSeconds(assignment) + this.getRouteDurationSeconds(assignment, source);
    }

    public static getVehicleLimit(
        type: string,
        assignmentOrValue: Assignment | number,
        limit: number | null,
        warningThreshold: number | null = null,
        source: string = 'base_route'
    ): VehicleLimitExceededType|null {
        // let limit: number | null = vehicle.getPropertyByName(type) ?? null;
        let assignment: Assignment | null = null;
        let value: number | null = null;
        let maxValue: number | null = null;
        let exceeded: boolean | null = null;
        let maxExceeded: boolean | null = null;
        let warningThresholdExceeded: boolean | null = null;
        let src: string = '';
        let alt: string = '';
        let color: string = this._VEHICLE_LIMIT_COLOR_ERROR;
        let count: number|null = null;
        let doubtReason: string|null = null;
        if (typeof(assignmentOrValue) === 'number') {
            value = assignmentOrValue;
        } else {
            assignment = assignmentOrValue;
        }
        switch (type) {
            case 'parcel_limit':
                alt = 'Vehicle_limit_exceeded_' + type;
                if (assignment) {
                    if (assignment.isRouteCalculated()) {
                        let alerts = this.getAlerts(assignment, source)?.parcels;
                        if (alerts) {
                            value = 0;
                            count = 0;
                            for (let i in alerts) {
                                value = Math.max(value, alerts[i].value || 0);
                                if (!alerts[i].fulfilled) {
                                    count++;
                                }
                            }
                        } else {
                            assignment.getCountParcels();
                        }
                    } else {
                        maxValue = assignment.getCountParcels();
                        value = Math.max(assignment.getCountParcelsToDeliver(), assignment.getCountParcelsToCollect());
                        if (assignment.getCountParcelsToDeliver() > 0 && assignment.getCountParcelsToCollect() > 0) {
                            doubtReason = 'no-route';
                        }
                    }
                }
                src = this._ALERT_ICON_PARCELS;
                break;
            case 'weight_limit':
                alt = 'Vehicle_limit_exceeded_' + type;
                if (assignment) {
                    if (assignment.isRouteCalculated()) {
                        let alerts = this.getAlerts(assignment, source)?.total_weight;
                        if (alerts) {
                            value = 0;
                            count = 0;
                            for (let i in alerts) {
                                value = Math.max(value, alerts[i].value || 0);
                                if (!alerts[i].fulfilled) {
                                    count++;
                                }
                            }
                        } else {
                            assignment.getTotalWeight();
                        }
                    } else {
                        maxValue = assignment.getTotalWeight();
                        value = Math.max(assignment.getTotalWeightToDeliver(), assignment.getTotalWeightToCollect());
                        if (assignment.getTotalWeightToDeliver() > 0 && assignment.getTotalWeightToCollect() > 0) {
                            doubtReason = 'no-route';
                        }
                    }
                }
                src =
                    this._ALERT_ICON_TOTAL_WEIGHT;
                break;
            case 'time_window':
                alt = 'time_window';
                if (assignment && assignment.isRouteCalculated() && this.hasTimeWindows(assignment, source)) {
                    value = this.getCountTimeWindowExceeded(assignment, source);
                } else {
                    return null;
                }
                src =
                    this._ALERT_ICON_TIME_WINDOW;
                break;

            case 'work_time':
                alt = 'work_time';
                if (assignment) {
                    if (assignment.isRouteCalculated()) {
                        value = this.getRouteDurationSeconds(assignment, source);
                    } else {
                        maxValue = this.getRouteDurationSeconds(assignment, source);
                        value = limit;
                        doubtReason = 'no-route';
                    }
                }
                src =
                    this._ALERT_ICON_WORK_TIME;
                break;

            case 'work_time_start':
                alt = 'work_time_start';
                if (assignment) {
                    value = this.getRouteStartSeconds(assignment);
                }
                src =
                    this._ALERT_ICON_WORK_TIME;
                break;

            case 'work_time_end':
                alt = 'work_time_end';
                if (assignment) {
                    if (assignment.isRouteCalculated()) {
                        value = this.getRouteEndSeconds(assignment, source);
                    } else {
                        maxValue = this.getRouteEndSeconds(assignment, source);
                        value = limit;
                        doubtReason = 'no-route';
                    }
                }
                src =
                    this._ALERT_ICON_WORK_TIME;
                break;
        }
        if (limit !== null) {
            if (value !== null) {
                if (type === 'work_time_start') {
                    exceeded = limit > value;
                } else {
                    exceeded = limit < value;
                }
            } else {
                exceeded = false;
                limit = 0;
                value = 0;
            }
            if (maxValue !== null) {
                maxExceeded = limit < maxValue;
            }
            if (warningThreshold) {
                warningThresholdExceeded = value !== null && value > limit * 0.9;
            }
            // if (type === 'work_time_start') {
            //     exceeded = !exceeded;
            // }
            color = exceeded ? this._VEHICLE_LIMIT_COLOR_ERROR : (maxExceeded || warningThresholdExceeded ? this._VEHICLE_LIMIT_COLOR_WARNING : this._VEHICLE_LIMIT_COLOR_SUCCESS);
        } else {
            exceeded = null;
            limit = 0;
            value = 0;
            color = this._VEHICLE_LIMIT_COLOR_UNSET;
        }

        alt =
            'Vehicle_limit_' +
            type +
            '_' +
            (exceeded ? 'exceeded' : exceeded === false ? (maxExceeded ? 'potentially_exceeded' : (warningThresholdExceeded ? 'nearly_exceeded' : 'met')) : 'unset');
        return {
            limit,
            value,
            maxValue,
            exceeded,
            maxExceeded,
            warningThresholdExceeded,
            src,
            alt,
            color,
            type,
            doubtReason,
            title: alt,
            count
        };
    }

    public static hasNonNormativeOrder(assignment: Assignment, source: string = 'base_route') {
        let non_normative_size = this.getAlerts(assignment, source)?.non_normative_size;
        if (assignment.isRouteCalculated() && non_normative_size) {
            let alerts = non_normative_size.filter((a: any) => a.value > 0);
            return alerts.length > 0;
        }
        return false;
    }

    public static getAlerts(assignment: Assignment, source: string = 'base_route') {
        let routeInfo = assignment.getRouteInfo();
        let alerts = null;
        if (source === 'base_route') {
            alerts = routeInfo?.alerts;
        } else if (source == 'current_in_realisation') {
            alerts = routeInfo?.current?.alerts;
        } else if (source == 'modified_by_operator') {
            alerts = assignment.getRouteModifiedByOperator()?.alerts;
        }
        return alerts;
    }

}
